<template>
  <div class="mobile">
    <input
      ref="fileLoader"
      type="file"
      :accept="`${acceptedVideoTypes},capture=user`"
      @change="(e) => loadVideo(e)"
      hidden
    />
    <div v-show="videoLoaded" class="video-container" :style="{ 'padding-top': videoAspectRatio }">
      <video ref="mobileVideoElement" poster="../assets/images/black.png" controls @loadedmetadata="onLoadedMetadata">
        Your browser does not support the <code>video</code> element.
      </video>
    </div>
    <TermsAndConditionsCheck
      v-if="
        (!videoLoaded && isIframed && !iframeParentIsAdminApp && !clickWrapFeatureEnabled) ||
        (!isIframed && !userInformationNeeded && !clickWrapFeatureEnabled)
      "
    />
    <div class="button-container">
      <Button :disabled="isLoading" id="btn-upload" :onclick="() => handleUploadRecording()" v-if="videoLoaded"
        >{{ t('UPLOAD VIDEO') }}
      </Button>
      <Button
        :variant="videoLoaded ? 'secondary' : 'primary'"
        :disabled="isLoading"
        :onclick="() => $refs.fileLoader.click()"
      >
        {{ t('RECORD OR SELECT') }}
      </Button>
      <a v-show="videoLoaded" ref="downloadableRecordedVideoElement" download class="btn secondary download">{{
        t('DOWNLOAD VIDEO')
      }}</a>
    </div>
  </div>
</template>

<script lang="ts">
import Button from '@/components/Button.vue';
import TermsAndConditionsCheck from '@/components/TermsAndConditionsCheck.vue';
import { computed, PropType, Ref, ref } from 'vue';
import { AppFreeAnswerParameters, Client, Question } from 'altru-types';
import { useStore } from 'vuex';
import { SetupProps, SetupReturnType } from '@/types/mobile';
import { useI18n } from 'vue-i18n';
import isAcceptedVideoType, { ACCEPTED_TYPES, USER_READABLE_ACCEPTED_TYPES } from '@/utils/isAcceptedVideoType';
import getVideoDuration from '@/utils/getVideoDuration';

export default {
  name: 'Mobile',
  props: {
    uploadVideo: {
      type: Function as PropType<(video: File) => void>,
    },
  },
  components: {
    Button,
    TermsAndConditionsCheck,
  },
  setup(props: SetupProps): SetupReturnType {
    const { t } = useI18n();
    const { getters, dispatch } = useStore();
    const userInformationNeeded: Ref<boolean> = computed(() => getters['clients/userInformationNeeded']);
    const questionInfo: Ref<Question> = computed(() => getters['questions/questionInfo']);
    const isLoading: Ref<boolean> = computed(() => getters['ui/isLoading']);
    const isIframed: Ref<boolean> = computed(() => getters['status/isIframed']);
    const iframeSource: Ref<AppFreeAnswerParameters['source']> = computed(() => getters['status/iframeSource']);
    const iframeParentIsAdminApp: Ref<boolean> = computed(() => getters['status/iframeParentIsAdminApp']);
    const client: Ref<Client | null> = computed(() => getters['clients/workspaceInfo']);
    const clickWrapFeatureEnabled: Ref<boolean> = computed(
      () => client.value?.feature_keys?.includes('click_wrap_terms_and_conditions') ?? false,
    );
    const fileLoader = ref<HTMLInputElement | null>(null);
    const mobileVideoElement = ref<HTMLVideoElement | null>(null);
    const downloadableRecordedVideoElement: Ref<HTMLAnchorElement | null> = ref<HTMLAnchorElement | null>(null);

    const video: Ref<File> = ref(new File([], ''));
    const videoLoaded: Ref<boolean> = ref(false);

    const loadVideo = (e: Event): void | Promise<void> => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        video.value = files[0];
        if (!isAcceptedVideoType(video.value.type)) {
          return dispatch('ui/setErrorMessage', {
            value: t('NOT SUPPORTED VIDEO FORMAT {0}', [acceptedVideoList]),
          });
        }
        if (mobileVideoElement.value) {
          mobileVideoElement.value.src = URL.createObjectURL(video.value);
          videoLoaded.value = true;

          if (downloadableRecordedVideoElement.value) {
            downloadableRecordedVideoElement.value.href = mobileVideoElement.value?.src;
          }

          setTimeout(() => {
            const uploadButton = document.getElementById('btn-upload');
            if (uploadButton) {
              uploadButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
              uploadButton.focus();
            }
          }, 1000);
        }
      }
    };

    const handleUploadRecording = async (): Promise<void> => {
      const videoName = video.value.name;
      const videoExtension = video.value.type;
      const file = new File(
        [video.value],
        videoName
          ? videoName
          : `altru-video-${questionInfo.value.id}-${Date.now()}${
              iframeSource.value ? `-${iframeSource.value}` : ''
            }.${videoExtension}`,
      );
      const meta = await getVideoDuration(file);

      if (questionInfo.value?.max_duration && questionInfo.value?.max_duration < meta.duration) {
        return await dispatch('ui/setErrorMessage', {
          value: t('VIDEO LENGTH EXCEEDED {0}', [
            new Date(questionInfo.value.max_duration * 1000).toISOString().substr(14, 5),
          ]),
          variant: 'error',
        });
      }
      return props.uploadVideo(file);
    };

    const acceptedVideoTypes = ACCEPTED_TYPES.join(',');
    const acceptedVideoList = USER_READABLE_ACCEPTED_TYPES.join(', ');

    const videoAspectRatio = ref<string>('');
    const onLoadedMetadata = (event: { target: HTMLVideoElement }) => {
      const targetEl = event.target;
      const value = (targetEl.videoHeight / targetEl.videoWidth) * 100;
      videoAspectRatio.value = `${value > 100 ? 100 : value}%`;
    };

    return {
      fileLoader,
      mobileVideoElement,
      videoLoaded,
      isLoading,
      loadVideo,
      handleUploadRecording,
      t,
      acceptedVideoTypes,
      downloadableRecordedVideoElement,
      userInformationNeeded,
      isIframed,
      iframeParentIsAdminApp,
      onLoadedMetadata,
      videoAspectRatio,
      clickWrapFeatureEnabled,
    };
  },
};
</script>

<style lang="scss" scoped>
.mobile {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 20px;

  .video-container {
    background: $black;
    border-radius: 0.5rem;
    height: 100%;
    position: relative;
    width: 100%;
  }

  video {
    border-radius: 0.5rem;
    box-shadow: 0 0 0 0.0625rem $light-grey;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }

  .button-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }
}
</style>
